/**
 * Screen — the one page frame (`KLQ-20260826-001`).
 *
 * **The problem it exists for.** Every list screen in this app used to hand its whole height to
 * the app's scrolling pane: the title, the filter bar, the toolbar and the table all travelled
 * up together, so reaching row two hundred meant losing the controls that act on it, and a page
 * that grew past the pane sometimes produced a second scrollbar beside the first. Each screen
 * had also solved the "how tall am I" question slightly differently — `flex: 1 0 auto` here, a
 * `min-height` there, a `100dvh` calculation in the rail — which is four answers to one
 * question and therefore three that are wrong somewhere.
 *
 * **The rule this frame enforces: exactly one thing on a screen scrolls, and it is the list.**
 * The head, the rail's own chrome and the toolbar stay where they were put. Anything an action
 * is drawn on is still on screen when the row it acts on is.
 *
 * FOUR SLOTS, AND WHY EACH ONE IS SEPARATE FROM THE BODY
 *
 * | Slot | What goes in it | Scrolls? |
 * |---|---|---|
 * | `head` | the screen's name, the arrangement toggle, the one primary action | never |
 * | `rail` | the slicer, or any left-hand furniture | itself, internally |
 * | `toolbar` | what acts on the list: filters, slice chips, writer buttons | never |
 * | `children` | the list, the table, the tree, the cards | **the only one** |
 *
 * `head` spans the full width above the rail; `toolbar` sits inside the rail's right-hand
 * column, above the body. That is the difference between *this screen* and *this list*, and
 * the two screens that have both need it drawn that way.
 *
 * **Below 900px it gives up and lets the page scroll**, which is deliberate rather than
 * unfinished: stacked, the rail is a screenful of filters above the list, and pinning a head
 * as well would leave a phone-sized window showing chrome and four rows. TWIN of the same
 * breakpoint in `components/slicer/Slicer.module.css` and the three feature stylesheets its
 * comment names.
 *
 * **The seam with the rail.** A rail written for the old model measured itself against the
 * viewport (`100dvh` minus the pane's offset) because nothing else could tell it how tall it
 * was allowed to be. Inside this frame its column has a definite height, so the frame sets
 * `--rail-max-height: 100%` and the rail uses it in place of the arithmetic. A rail outside
 * this frame reads the same variable, finds it unset, and keeps the old default — which is why
 * the frame can be adopted one screen at a time.
 */

import type { ReactNode } from "react";

import { CARDS, useMediaQuery } from "@/lib/media";
import styles from "./Screen.module.css";

export interface ScreenProps {
  /** The screen's own header. Full width, above the rail. Never scrolls. */
  readonly head?: ReactNode | undefined;
  /** Left-hand furniture — the slicer, on the screens that have one. Owns its own scrolling. */
  readonly rail?: ReactNode | undefined;
  /** What acts on the list: filters, slice chips, writer buttons. Never scrolls. */
  readonly toolbar?: ReactNode | undefined;
  /** The list. The only region on the screen with a scrollbar. */
  readonly children: ReactNode;
}

export function Screen({ head, rail, toolbar, children }: ScreenProps) {
  /**
   * **Under 600px the rail moves into the toolbar row** (owner, 2026-09-21).
   *
   * At that width the rail is not a rail: `components/slicer` has already swapped itself for a
   * 32px icon button, and a band of its own for one 32px button is a whole row of a phone spent
   * on a control that fits beside the one next to it. So the frame stops giving it a band and
   * hands it to the toolbar, which is the row that already holds *what acts on the list*.
   *
   * 600px and not `STACKED`'s 900: at 900 the rail is still a block of filters worth its own
   * band above the list, and it is only where the table becomes cards that it shrinks to a
   * button. Same threshold, same reason, same `CARDS` — see `lib/media`.
   *
   * A rail that is *not* a small control at this width would be squeezed into a toolbar cell,
   * so this is a contract between the frame and `Slicer`, not a general rule about rails. It is
   * written down here because there is nothing in the types that says it.
   */
  const inToolbar = useMediaQuery(CARDS) && rail !== undefined;

  return (
    <div className={styles.screen}>
      {head === undefined ? null : <div className={styles.head}>{head}</div>}

      <div
        className={[
          styles.split,
          rail === undefined ? "" : styles.withRail,
        ].join(" ")}
      >
        {rail === undefined || inToolbar ? null : (
          <div className={styles.rail}>{rail}</div>
        )}

        <div className={styles.pane}>
          {toolbar === undefined && !inToolbar ? null : (
            <div
              className={[styles.toolbar, inToolbar ? styles.toolbarWithRail : ""]
                .filter(Boolean)
                .join(" ")}
            >
              {!inToolbar ? (
                toolbar
              ) : (
                <>
                  <div className={styles.toolbarRail}>{rail}</div>
                  {/* Wrapped, because the two-column form needs the toolbar's own contents to
                      be one cell however many nodes a screen passed. Only on this branch: the
                      unwrapped form is a grid whose rows a screen may be relying on. */}
                  <div className={styles.toolbarMain}>{toolbar}</div>
                </>
              )}
            </div>
          )}
          <div className={styles.body}>{children}</div>
        </div>
      </div>
    </div>
  );
}
